import { Check, ChevronsUpDown } from "lucide-react";
import { FieldPath, UseFormReturn } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { cn } from "@/lib/utils";

export const FormEnumMultiselect = <
  T extends Record<string, unknown>,
  E extends string | number,
>({
  className,
  disabled,
  enumToLabel,
  fieldName,
  form,
  inputClassName,
  label,
  maxSelections,
  options,
}: {
  className?: string;
  disabled?: boolean;
  enumToLabel: Record<E, string>;
  fieldName: FieldPath<T>;
  form: UseFormReturn<T>;
  inputClassName?: string;
  label: string;
  maxSelections?: number;
  options: { value: E; label: string }[];
}) => {
  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => {
        // Type assertion: We know from our generic constraint that the value is E[]
        const value = field.value as E[] | undefined;

        return (
          <FormItem className={cn("w-full text-slate-700", className)}>
            <FormLabel>{label}</FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    disabled={disabled}
                    variant="outlineSecondary"
                    className={cn(
                      "w-full justify-between font-normal",
                      inputClassName,
                    )}
                  >
                    <span className="truncate">
                      {value && value.length > 0
                        ? value
                            .map((enumValue) => enumToLabel[enumValue])
                            .join(", ")
                        : "-"}
                    </span>
                    <ChevronsUpDown className="opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                <Command>
                  <CommandList>
                    <CommandGroup>
                      {options.map((option) => {
                        const isSelected = value?.includes(option.value);
                        return (
                          <CommandItem
                            key={String(option.value)}
                            className="text-slate-700"
                            onSelect={() => {
                              if (
                                !isSelected &&
                                maxSelections !== undefined &&
                                (value?.length ?? 0) >= maxSelections
                              ) {
                                return;
                              }
                              const newValue = isSelected
                                ? value?.filter((v) => v !== option.value)
                                : [...(value ?? []), option.value];
                              field.onChange(newValue);
                            }}
                          >
                            <Check className={cn(!isSelected && "invisible")} />
                            {option.label}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
